import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { IScannerControls } from '@zxing/browser';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';

interface QrScannerDialogProps {
    open: boolean;
    onClose: () => void;
    onDecoded: (text: string) => void;
}

/**
 * Camera-based QR scanner. Streams the rear camera into a <video> and reports the
 * first decoded payload via `onDecoded`. Requires a secure context (HTTPS or
 * localhost) — `getUserMedia` is unavailable over plain HTTP. Releases the camera
 * on close/unmount.
 */
export default function QrScannerDialog({
    open,
    onClose,
    onDecoded
}: QrScannerDialogProps) {
    const intl = useIntl();
    const videoRef = useRef<HTMLVideoElement>(null);
    const controlsRef = useRef<IScannerControls | null>(null);
    const decodedRef = useRef(false);
    // Keep the latest callback without restarting the camera each render.
    const onDecodedRef = useRef(onDecoded);
    const [error, setError] = useState<string | null>(null);
    // Bumping this re-runs the camera effect — used to retry after a denial.
    const [retryNonce, setRetryNonce] = useState(0);

    useEffect(() => {
        onDecodedRef.current = onDecoded;
    }, [onDecoded]);

    function handleRetry() {
        setError(null);
        setRetryNonce((nonce) => nonce + 1);
    }

    useEffect(() => {
        if (!open) {
            return;
        }
        decodedRef.current = false;
        let cancelled = false;

        // @zxing/browser is heavy — load it only when the scanner opens.
        (async () => {
            try {
                const { BrowserQRCodeReader } = await import('@zxing/browser');
                const reader = new BrowserQRCodeReader();
                const controls = await reader.decodeFromConstraints(
                    { video: { facingMode: 'environment' } },
                    videoRef.current ?? undefined,
                    (result) => {
                        if (result && !decodedRef.current) {
                            decodedRef.current = true;
                            onDecodedRef.current(result.getText());
                        }
                    }
                );
                if (cancelled) {
                    controls.stop();
                    return;
                }
                controlsRef.current = controls;
            } catch {
                if (!cancelled) {
                    setError(intl.formatMessage({ id: 'auth.qr.cameraError' }));
                }
            }
        })();

        return () => {
            cancelled = true;
            controlsRef.current?.stop();
            controlsRef.current = null;
        };
    }, [open, retryNonce, intl]);

    return (
        <Dialog open={open} onClose={onClose} fullScreen>
            <DialogTitle>
                <FormattedMessage id="auth.qr.scan.title" />
            </DialogTitle>
            <DialogContent
                sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >
                {error ? (
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 200,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 2
                        }}
                    >
                        <Alert
                            severity="error"
                            sx={{ width: '100%', maxWidth: 480 }}
                        >
                            {error}
                        </Alert>
                        <Button variant="contained" onClick={handleRetry}>
                            <FormattedMessage id="auth.qr.requestAccess" />
                        </Button>
                    </Box>
                ) : (
                    <>
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'text.secondary'
                            }}
                        >
                            <FormattedMessage id="auth.qr.scan.hint" />
                        </Typography>
                        <Box
                            sx={{
                                flex: 1,
                                minHeight: 300,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'black',
                                borderRadius: 1,
                                overflow: 'hidden'
                            }}
                        >
                            <video
                                ref={videoRef}
                                autoPlay
                                muted
                                playsInline
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                }}
                            />
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={onClose}>
                    <FormattedMessage id="common.cancel" />
                </Button>
            </DialogActions>
        </Dialog>
    );
}
